// 1st/2nd Half Goals — bars view (Phase 3). A 1H/2H split bar for Scored + one
// for Conceded. `.hg-1h`/`.hg-2h` have CSS widths (0% / 100%) and no transition,
// so — like HtFtBars/OverUnder — React owns the shell (labels) and D3 animates
// the two fill widths per row. Keyed by fixture+team (by the parent), so those
// changes reset the fills, matching the legacy cacheKey rebuild; a timeline
// change keeps the mount and animates current→new. Same DOM as the legacy render.

import { useEffect, useRef } from "react";
import { select } from "d3-selection";
import "d3-transition";
import { easeCubicInOut } from "d3-ease";
import { shapeHalfGoalsBars, type HalfSplit } from "@bento/shared";
import { useWidgetData } from "../../data/useWidgetData.js";
import { useFixtureKey } from "../../data/useFixtureKey.js";
import WidgetSkeleton from "./WidgetSkeleton.js";
import WidgetError from "./WidgetError.js";

const DURATION = 300;

export interface HalfGoalsBarsProps {
  fixture: string;
  team?: string | null;
  timeline?: string | null;
}

export default function HalfGoalsBars({
  fixture,
  team,
  timeline,
}: HalfGoalsBarsProps) {
  const wrapRef = useRef<HTMLDivElement>(null);
  // Both view children fetch the one `half-goals` envelope (React Query dedupes
  // the shared key, so flipping views is a cache hit); this child reads `bars`.
  const { data, isPending, isError, refetch } = useWidgetData(
    "half-goals",
    useFixtureKey(fixture)
  );
  const parts = (fixture || "Arsenal v Everton").split(" v ");
  const chosen = team || (parts[0] ?? "").trim();
  const view = shapeHalfGoalsBars(data?.bars ?? {}, chosen, timeline);

  const sig = JSON.stringify(view);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap || !view) return;
    const rows: [string, HalfSplit][] = [
      ["sc", view.scored],
      ["co", view.conceded],
    ];
    rows.forEach(([key, s]) => {
      const row = wrap.querySelector<HTMLElement>(`.sc-stat[data-stat="${key}"]`);
      if (!row) return;
      const anim = (sel: string, pct: number) =>
        select(row.querySelector<HTMLElement>(sel)!)
          .transition()
          .duration(DURATION)
          .ease(easeCubicInOut)
          .style("width", pct + "%");
      anim(".hg-1h", s.p1);
      anim(".hg-2h", s.p2);
    });
    // `sig` covers both rows' splits — the complete dependency.
  }, [sig]);

  if (isPending) return <WidgetSkeleton />;
  if (isError) return <WidgetError onRetry={refetch} />;

  if (!view) {
    return (
      <div className="data-wrapper top-align">
        <div className="module-empty">No data</div>
      </div>
    );
  }

  const row = (key: "sc" | "co", label: string, s: HalfSplit) => (
    <div className="sc-stat" data-stat={key}>
      <div className="sc-stat-header">
        <span className="sc-stat-name">{label}</span>
      </div>
      <div className="hg-bar-labels">
        <span className="hg-label-1h">{`1H: ${s.p1}%`}</span>
        <span className="hg-label-2h">{`2H: ${s.p2}%`}</span>
      </div>
      <div className="hg-split-track">
        <div className="hg-1h bento-fill" />
        <div className="hg-2h bento-fill" />
      </div>
    </div>
  );

  return (
    <div className="data-wrapper top-align" ref={wrapRef}>
      <div className="sc-block">
        <div className="sc-team-label">{chosen}</div>
        {row("sc", "Scored", view.scored)}
        {row("co", "Conceded", view.conceded)}
      </div>
    </div>
  );
}
